import React from 'react';
import {
  CalendarBlank,
  Camera,
  ChatCircleText,
  HeartStraight,
  House,
  MapPin,
} from '@phosphor-icons/react';
import { buildThemeCssVariables } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { resolveMediaUrl } from '../utils/media';
import useEventTypeConfig from '../hooks/useEventTypeConfig';
import { formatEventNameList } from '../config/eventTypes';

const readableForeground = (hex = '#000000') => {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((character) => character.repeat(2)).join('')
    : normalized;
  const red = parseInt(value.slice(0, 2), 16) || 0;
  const green = parseInt(value.slice(2, 4), 16) || 0;
  const blue = parseInt(value.slice(4, 6), 16) || 0;
  return ((red * 0.2126) + (green * 0.7152) + (blue * 0.0722)) / 255 > 0.58
    ? '#1F2933'
    : '#FFFFFF';
};

export default function ThemeLivePreview({ event, theme, backgroundImage, description, className }) {
  const eventTypeConfig = useEventTypeConfig(event);
  if (!theme) return null;

  const colors = theme.colors || {};
  const fonts = theme.fonts || {};
  const displayNames = eventTypeConfig.isWedding
    ? [event?.bride_name, event?.groom_name].filter(Boolean).join(' & ')
    : eventTypeConfig.isBirthday
      ? formatEventNameList(event?.honoree_names)
    : (event?.host_names || []).join(' & ');
  const eventName = event?.name || eventTypeConfig.labels.guestHomeTitleFallback;
  const heroTitle = displayNames || eventName;
  const primaryForeground = readableForeground(colors.primary);
  const secondaryForeground = readableForeground(colors.secondary);
  const resolvedBackgroundImage = resolveMediaUrl(backgroundImage);

  return (
    <section
      className={cn('relative overflow-hidden rounded-xl border bg-background text-foreground shadow-sm', className)}
      data-testid="live-theme-preview"
      style={{
        ...buildThemeCssVariables(theme),
        borderColor: colors.primary,
        fontFamily: fonts.body,
      }}
    >
      {resolvedBackgroundImage && (
        <div
          className="pointer-events-none absolute inset-0 opacity-20"
          data-testid="live-theme-preview-background"
          style={{
            backgroundImage: `url(${resolvedBackgroundImage})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        />
      )}

      <div className="relative">
        <div className="flex items-center justify-between gap-4 bg-[var(--theme-sidebar)] px-4 py-3 text-[var(--theme-sidebar-text)] sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary text-secondary-foreground">
              <HeartStraight className="h-4 w-4" weight="fill" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-serif text-base font-semibold">{heroTitle}</p>
              <p className="truncate text-[0.5625rem] uppercase tracking-[0.22em] text-[var(--theme-sidebar-muted)]">{eventName}</p>
            </div>
          </div>
          <div className="hidden items-center gap-5 text-[0.625rem] uppercase tracking-[0.18em] text-[var(--theme-sidebar-muted)] 2xl:flex">
            <span className="text-[var(--theme-sidebar-text)]">Home</span>
            <span>Itinerary</span>
            <span>Photos</span>
            <span>RSVP</span>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="overflow-hidden rounded-xl border border-border bg-card/90 shadow-sm backdrop-blur">
            <div
              className="relative overflow-hidden px-5 py-7 sm:px-8 sm:py-9"
              style={{
                background: `linear-gradient(125deg, ${colors.primary}, ${colors.secondary})`,
                color: primaryForeground,
              }}
            >
              <div className="absolute -right-10 -top-16 h-40 w-40 rounded-full border border-white/20" />
              <div className="absolute -right-2 -top-8 h-28 w-28 rounded-full border border-white/15" />
              <p className="relative text-[0.5625rem] uppercase tracking-[0.3em] opacity-75">You are invited</p>
              <h3 className="relative mt-2 font-serif text-2xl sm:text-3xl" style={{ fontFamily: fonts.heading }}>
                {heroTitle}
              </h3>
              <p className="relative mt-2 max-w-lg text-xs leading-5 opacity-80">
                A joyful celebration with the people who make every moment meaningful.
              </p>
              <div className="relative mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-full bg-primary px-4 py-2 text-xs font-semibold shadow-sm"
                  style={{ color: primaryForeground }}
                >
                  Update RSVP
                </button>
                <button
                  type="button"
                  className="rounded-full bg-secondary px-4 py-2 text-xs font-semibold shadow-sm"
                  style={{ color: secondaryForeground }}
                >
                  View Itinerary
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 divide-x divide-border bg-card/85">
              {[
                { icon: CalendarBlank, label: 'Date', value: '18 Feb' },
                { icon: MapPin, label: 'Venue', value: 'Grand Hall' },
                { icon: Camera, label: 'Photos', value: '24 moments' },
              ].map((item) => (
                <div key={item.label} className="min-w-0 p-3 sm:p-4">
                  <item.icon className="h-4 w-4 text-primary" />
                  <p className="mt-2 text-[0.5rem] uppercase tracking-[0.2em] text-muted-foreground">{item.label}</p>
                  <p className="mt-1 truncate font-serif text-xs sm:text-sm" style={{ fontFamily: fonts.heading }}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-card/85 p-4 backdrop-blur">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">
                    <CalendarBlank className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-serif text-sm" style={{ fontFamily: fonts.heading }}>Today&apos;s celebration</p>
                    <p className="text-[0.625rem] text-muted-foreground">Ceremony · 6:30 PM</p>
                  </div>
                </div>
                <span className="rounded-full bg-secondary/20 px-2 py-1 text-[0.5625rem] font-medium text-foreground">Festive</span>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-card/85 p-4 backdrop-blur">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-secondary/20 text-foreground">
                  <ChatCircleText className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-serif text-sm" style={{ fontFamily: fonts.heading }}>Share a wish</p>
                  <p className="text-[0.625rem] text-muted-foreground">Leave a note for the hosts</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-[var(--theme-sidebar-border)] bg-[var(--theme-sidebar)] px-4 py-3 text-[var(--theme-sidebar-muted)] sm:px-6">
          <span className="inline-flex items-center gap-2 text-[0.5625rem] uppercase tracking-[0.18em]">
            <House className="h-3.5 w-3.5 text-secondary" /> Guest view
          </span>
          <span className="max-w-[60%] truncate text-right text-[0.5625rem]">{description}</span>
        </div>
      </div>
    </section>
  );
}
